/**
 * The 20 UI languages WarnixAI ships (decided 2026-09-02). Hebrew is the
 * product default; English is the translation fallback.
 *
 * `intlLocale` is what Intl.* formatters receive — kept separate from the
 * i18next code so e.g. Hebrew formats dates the Israeli way (he-IL) while the
 * translation bundle is simply "he".
 */
export type Dir = "rtl" | "ltr";

export interface LanguageMeta {
  code: string;
  nativeName: string;
  dir: Dir;
  intlLocale: string;
}

export const LANGUAGES: readonly LanguageMeta[] = [
  { code: "he", nativeName: "עברית", dir: "rtl", intlLocale: "he-IL" },
  { code: "en", nativeName: "English", dir: "ltr", intlLocale: "en-US" },
  { code: "ar", nativeName: "العربية", dir: "rtl", intlLocale: "ar" },
  { code: "ru", nativeName: "Русский", dir: "ltr", intlLocale: "ru-RU" },
  { code: "zh", nativeName: "中文", dir: "ltr", intlLocale: "zh-CN" },
  { code: "th", nativeName: "ไทย", dir: "ltr", intlLocale: "th-TH" },
  { code: "ro", nativeName: "Română", dir: "ltr", intlLocale: "ro-RO" },
  { code: "tr", nativeName: "Türkçe", dir: "ltr", intlLocale: "tr-TR" },
  { code: "uk", nativeName: "Українська", dir: "ltr", intlLocale: "uk-UA" },
  { code: "hi", nativeName: "हिन्दी", dir: "ltr", intlLocale: "hi-IN" },
  { code: "es", nativeName: "Español", dir: "ltr", intlLocale: "es-ES" },
  { code: "pt", nativeName: "Português", dir: "ltr", intlLocale: "pt-BR" },
  { code: "fr", nativeName: "Français", dir: "ltr", intlLocale: "fr-FR" },
  { code: "de", nativeName: "Deutsch", dir: "ltr", intlLocale: "de-DE" },
  { code: "it", nativeName: "Italiano", dir: "ltr", intlLocale: "it-IT" },
  { code: "pl", nativeName: "Polski", dir: "ltr", intlLocale: "pl-PL" },
  { code: "tl", nativeName: "Filipino", dir: "ltr", intlLocale: "fil-PH" },
  { code: "am", nativeName: "አማርኛ", dir: "ltr", intlLocale: "am-ET" },
  { code: "bg", nativeName: "Български", dir: "ltr", intlLocale: "bg-BG" },
  { code: "vi", nativeName: "Tiếng Việt", dir: "ltr", intlLocale: "vi-VN" },
] as const;

export const LANGUAGE_CODES: readonly string[] = LANGUAGES.map((l) => l.code);
export const RTL_LANGUAGES: readonly string[] = LANGUAGES.filter((l) => l.dir === "rtl").map((l) => l.code);
export const DEFAULT_LANGUAGE = "he";
export const FALLBACK_LANGUAGES: readonly string[] = ["en", "he"];

/** localStorage key holding the user's explicit choice (survives logout). */
export const LANGUAGE_STORAGE_KEY = "warnix_v2_lang";

const BY_CODE: Record<string, LanguageMeta> = Object.fromEntries(LANGUAGES.map((l) => [l.code, l]));

/** Legacy / alias tags browsers still emit → our code. */
const ALIASES: Record<string, string> = {
  iw: "he",
  fil: "tl",
  in: "id", // not supported; falls through to default — listed so it is not mis-mapped
};

export function isSupportedLanguage(code: unknown): code is string {
  return typeof code === "string" && Object.prototype.hasOwnProperty.call(BY_CODE, code);
}

export function languageMeta(code: string): LanguageMeta {
  return BY_CODE[code] ?? BY_CODE[DEFAULT_LANGUAGE];
}

export function dirFor(code: string): Dir {
  return languageMeta(code).dir;
}

/**
 * Map any BCP-47-ish tag ("en-US", "zh-Hans-CN", "iw", "pt_BR") to one of the
 * 20 supported codes, or the default language when nothing matches.
 */
export function normalizeLanguage(tag: string | null | undefined): string {
  if (!tag || typeof tag !== "string") return DEFAULT_LANGUAGE;
  const primary = tag.trim().toLowerCase().split(/[-_]/)[0];
  if (!primary) return DEFAULT_LANGUAGE;
  const mapped = ALIASES[primary] ?? primary;
  return isSupportedLanguage(mapped) ? mapped : DEFAULT_LANGUAGE;
}
